import { createApi } from "@reduxjs/toolkit/query/react";
import baseQuery from "./baseQuery";
import { API_SERVER } from "../utils/urlBack_End";

export const accesoClientesApi = createApi({
  reducerPath: "accesoClientesApi",
  baseQuery: baseQuery(`${API_SERVER}/accesoClientes`),

  tagTypes: [
    // Información del Cliente
    "Cliente",
    "TareasCliente",
    "SubtareasCliente",
    "SociosCliente",
    "ContratosCliente",
    "PlanesCliente",
    "ClienteEmailCC",
    "DatosPersonalesCliente",

    // Facturas
    "FacturasPendientes",
    "FacturasImpagas",
    "FacturasPlanes",
    "FacturasTareas",
    "FacturaEspecifica",

    // Pagos
    "PagosPlanes",
    "PagosTareas",

    "facturaDetalle",

    // WebPay
    "WebPay",
  ],

  endpoints: (builder) => ({
    // ========================================================================
    // INFORMACIÓN DEL CLIENTE
    // ========================================================================

    /**
     * Obtiene los datos completos de un cliente específico
     * @param {string} id - RUT del cliente
     * @returns Cliente con todos sus datos
     */
    obtenerClienteEspecifico: builder.query({
      query: (id) => `/${id}`,
      providesTags: (result, error, id) => [{ type: "Cliente", id }],
    }),

    /**
     * Actualiza los datos personales del cliente (campos permitidos solamente)
     * @param {string} rut - RUT del cliente
     * @param {object} datos - Campos editables: nombre, giro, telefono, correo, correoFacturacion, contacto, dirección, comuna
     */
    actualizarDatosPersonalesCliente: builder.mutation({
      query: ({ rut, datos }) => ({
        url: `/${rut}/datos-personales`,
        method: "PATCH",
        body: { datos },
      }),
      invalidatesTags: (result, error, { rut }) => [
        { type: "Cliente", id: rut },
      ],
    }),

    /**
     * Obtiene todas las tareas asignadas a un cliente
     * @param {string} id - RUT del cliente
     * @returns Array de tareas del cliente
     */
    obtenerTareasCliente: builder.query({
      query: (id) => `/tareas/${id}`,
      providesTags: (result, error, id) => [
        { type: "TareasCliente", id },
        "TareasCliente",
      ],
    }),

    /**
     * Obtiene las subtareas de un cliente
     * @param {string} id - RUT del cliente
     * @returns Array de subtareas
     */
    obtenerSubtareasCliente: builder.query({
      query: (id) => `/subtareas/${id}`,
      providesTags: (result, error, id) => [
        { type: "SubtareasCliente", id },
        "SubtareasCliente",
      ],
    }),

    /**
     * Obtiene los socios de un cliente
     * @param {string} id - RUT del cliente
     * @returns Array de socios del cliente
     */
    obtenerSociosCliente: builder.query({
      query: (id) => `/socios/${id}`,
      providesTags: (result, error, id) => [
        { type: "SociosCliente", id },
        "SociosCliente",
      ],
    }),

    /**
     * Obtiene los contratos de un cliente
     * @param {string} id - RUT del cliente
     * @returns Array de contratos del cliente
     */
    obtenerContratosCliente: builder.query({
      query: (id) => `/contrato/${id}`,
      providesTags: (result, error, id) => [
        { type: "ContratosCliente", id },
        "ContratosCliente",
      ],
    }),

    /**
     * Obtiene los planes asociados a un cliente
     * @param {string} id - RUT del cliente
     * @returns Array de planes del cliente
     */
    obtenerPlanesCliente: builder.query({
      query: (id) => `/planes/${id}`,
      providesTags: (result, error, id) => [
        { type: "PlanesCliente", id },
        "PlanesCliente",
      ],
    }),

    // ========================================================================
    // FACTURAS
    // ========================================================================

    /**
     * Obtiene todas las facturas pendientes de pago de un cliente
     * @param {string} id - RUT del cliente
     * @returns Array de facturas pendientes
     */
    obtenerFacturasPendientesPago: builder.query({
      query: (id) => `/facturas/pendientePago/${id}`,
      providesTags: (result, error, id) => [
        { type: "FacturasPendientes", id },
        "FacturasPendientes",
      ],
    }),

    /**
     * Obtiene todas las facturas impagas del cliente (planes + tareas), sin
     * filtro de vencimiento. Cada item incluye un flag `vencida: 0|1`
     * calculado en backend según el periodo de vencimiento del cliente.
     * @param {string} id - RUT del cliente
     * @returns Array de facturas impagas con flag vencida
     */
    obtenerFacturasImpagas: builder.query({
      query: (id) => `/facturas/impagas/${id}`,
      providesTags: (result, error, id) => [
        { type: "FacturasImpagas", id },
        "FacturasImpagas",
      ],
    }),

    /**
     * Obtiene una factura específica (puede ser de plan o tarea)
     * @param {object} params
     * @param {number} params.idFactura - ID de la factura
     * @param {string} params.tipoFactura - "planes" o "tareas"
     * @returns Datos de la factura específica
     */
    obtenerFacturaEspecifica: builder.query({
      query: ({ idFactura, tipoFactura }) =>
        `/facturas/${idFactura}/${tipoFactura}`,
      providesTags: (result, error, { idFactura, tipoFactura }) => [
        { type: "FacturaEspecifica", id: `${tipoFactura}-${idFactura}` },
        "FacturaEspecifica",
      ],
    }),

    /**
     * Obtiene todas las facturas de planes de un cliente
     * @param {string} id - RUT del cliente
     * @returns Array de facturas de planes
     */
    obtenerFacturasPlanesCliente: builder.query({
      query: (id) => `/facturas/planes/${id}`,
      providesTags: (result, error, id) => [
        { type: "FacturasPlanes", id },
        "FacturasPlanes",
      ],
    }),

    /**
     * Obtiene todas las facturas de tareas de un cliente
     * @param {string} id - RUT del cliente
     * @returns Array de facturas de tareas
     */
    obtenerFacturasTareasCliente: builder.query({
      query: (id) => `/facturas/tareas/${id}`,
      providesTags: (result, error, id) => [
        { type: "FacturasTareas", id },
        "FacturasTareas",
      ],
    }),

    // ========================================================================
    // PAGOS
    // ========================================================================

    /**
     * Obtiene el detalle de pagos de una factura de planes (YA EXISTÍA)
     * @param {number} folio - Folio de la factura de plan
     * @returns Array de pagos de la factura
     */
    obtenerDetalleFacturaClientePlanes: builder.query({
      query: (folio) => `/pagos/planes/${folio}`,
      providesTags: (result, error, folio) => [
        { type: "facturaDetalle", id: `CLIENTE-PLAN-${folio}` }, // Legacy
        { type: "PagosPlanes", id: folio },
        "PagosPlanes",
      ],
    }),

    /**
     * Obtiene los pagos de una factura de tareas
     * @param {number} idFactura - ID de la factura de tarea
     * @returns Array de pagos de la factura de tarea
     */
    obtenerPagosTareasFactura: builder.query({
      query: (idFactura) => `/pagos/tareas/${idFactura}`,
      providesTags: (result, error, idFactura) => [
        { type: "PagosTareas", id: idFactura },
        "PagosTareas",
      ],
    }),

    // ========================================================================
    // MUTATIONS (Acciones que modifican datos)
    // ========================================================================

    /**
     * Adjunta la metadata de un archivo (previamente subido a Dropbox) a una tarea
     * NOTA: El archivo debe estar subido a Dropbox ANTES de llamar este endpoint
     *
     * @param {object} params
     * @param {number} params.idTarea - ID de la tarea
     * @param {string} params.nombreArchivo - Nombre del archivo subido
     * @param {string} params.tipoArchivo - Tipo/extensión del archivo
     * @param {string} params.link - URL del archivo en Dropbox
     * @returns Respuesta del servidor confirmando el registro
     */
    adjuntarArchivoTarea: builder.mutation({
      query: ({ idTarea, nombreArchivo, tipoArchivo, link }) => ({
        url: `/tareas/archivo/${idTarea}`,
        method: "POST",
        body: { nombreArchivo, tipoArchivo, link },
      }),
      invalidatesTags: ["TareasCliente"],
    }),

    /**
     * Registra un pago WebPay confirmado en la BD.
     * Llamar SOLO después de un commit exitoso (response_code === 0).
     * @param {{ tipoFactura, idFactura, idTarea?, monto, sucursal, datosTransbank }} body
     */
    registrarPagoWebPay: builder.mutation({
      query: (body) => ({
        url: "/pagos/webpay/registrar",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        "FacturasPlanes",
        "FacturasTareas",
        "FacturasPendientes",
        "FacturasImpagas",
        "FacturaEspecifica",
        "PagosPlanes",
        "PagosTareas",
      ],
    }),

    /**
     * Registra múltiples pagos WebPay en una sola transacción.
     * Llamar SOLO después de un commit exitoso (response_code === 0).
     * @param {{ facturas: Array, montoTotal: number, datosTransbank: object }} body
     */
    registrarPagoMultiple: builder.mutation({
      query: (body) => ({
        url: "/pagos/webpay/registrar-multiple",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        "FacturasPlanes",
        "FacturasTareas",
        "FacturasPendientes",
        "FacturasImpagas",
        "FacturaEspecifica",
        "PagosPlanes",
        "PagosTareas",
      ],
    }),
    /**
     * Cambio forzado de contraseña post-login.
     * El RUT se obtiene del JWT en el backend — no se envía en el body.
     */
    cambiarContrasenaForzada: builder.mutation({
      query: (body) => ({
        url: "/portal/cambiarContrasena",
        method: "PUT",
        body,
      }),
    }),

    // ========================================================================
    // EMAILS EN COPIA (CC) DEL CLIENTE
    // ========================================================================

    /**
     * Obtiene los correos CC asociados al cliente
     * @param {string} rutCliente - RUT del cliente
     */
    obtenerEmailsCC: builder.query({
      query: (rutCliente) => `/emails-cc/${rutCliente}`,
      providesTags: ["ClienteEmailCC"],
      transformResponse: (response) => response.data,
    }),

    /**
     * Registra un nuevo correo CC para el cliente
     */
    agregarEmailCC: builder.mutation({
      query: (data) => ({
        url: "/emails-cc",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["ClienteEmailCC"],
    }),

    /**
     * Actualiza un correo CC existente
     */
    actualizarEmailCC: builder.mutation({
      query: ({ rutCliente, idEmailCC, ...data }) => ({
        url: `/emails-cc/${rutCliente}/${idEmailCC}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["ClienteEmailCC"],
    }),

    /**
     * Alterna el estado activo/inactivo de un correo CC
     */
    toggleActivoEmailCC: builder.mutation({
      query: ({ rutCliente, idEmailCC, ...data }) => ({
        url: `/emails-cc/${rutCliente}/${idEmailCC}/toggle`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["ClienteEmailCC"],
    }),
  }),
});

export const {
  // Queries - Información del Cliente
  useObtenerClienteEspecificoQuery,
  useLazyObtenerClienteEspecificoQuery,

  useObtenerTareasClienteQuery,
  useLazyObtenerTareasClienteQuery,

  useObtenerSubtareasClienteQuery,
  useLazyObtenerSubtareasClienteQuery,

  useObtenerSociosClienteQuery,
  useLazyObtenerSociosClienteQuery,

  useObtenerContratosClienteQuery,
  useLazyObtenerContratosClienteQuery,

  useObtenerPlanesClienteQuery,
  useLazyObtenerPlanesClienteQuery,

  // Queries - Facturas
  useObtenerFacturasPendientesPagoQuery,
  useLazyObtenerFacturasPendientesPagoQuery,

  useObtenerFacturasImpagasQuery,
  useLazyObtenerFacturasImpagasQuery,

  useObtenerFacturaEspecificaQuery,
  useLazyObtenerFacturaEspecificaQuery,

  useObtenerFacturasPlanesClienteQuery,
  useLazyObtenerFacturasPlanesClienteQuery,

  useObtenerFacturasTareasClienteQuery,
  useLazyObtenerFacturasTareasClienteQuery,

  // Queries - Pagos
  useObtenerDetalleFacturaClientePlanesQuery,
  useLazyObtenerDetalleFacturaClientePlanesQuery,

  useObtenerPagosTareasFacturaQuery,
  useLazyObtenerPagosTareasFacturaQuery,

  // Mutations
  useAdjuntarArchivoTareaMutation,
  useRegistrarPagoWebPayMutation,
  useRegistrarPagoMultipleMutation,
  useCambiarContrasenaForzadaMutation,

  // Emails CC
  useObtenerEmailsCCQuery,
  useLazyObtenerEmailsCCQuery,
  useAgregarEmailCCMutation,
  useActualizarEmailCCMutation,
  useToggleActivoEmailCCMutation,

  // Datos Personales
  useActualizarDatosPersonalesClienteMutation,
} = accesoClientesApi;
